import React, { useEffect, useState, useRef, useMemo } from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { Loader2, CalendarIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { format } from 'date-fns'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent } from '@/components/ui/card'
import {
  EditableDataGrid,
  GridColumn,
  ProductSearchResult
} from '@/app/pages/sales-invoices/components/EditableDataGrid'
import { DebitNoteFormValues, debitNoteSchema } from './schema'
import { Site } from '@/app/types/site'
import { Item } from '@/app/types/item'
import { VoucherResponse, VoucherItemResponse } from '@/app/types/voucher'
import { Godown } from '@/app/types/inventory'
import { cn } from '@/lib/utils'
import { Decimal } from 'decimal.js'
import { getItemPriceDetails, PricingContext, ItemPriceDetails } from '@/app/lib/pricingUtils'
import { usePreferenceStore } from '@/app/store/preferenceStore'
import { PartySelector } from '@/app/components/common/PartySelector'

// Define CalculationItem type at component level
type CalculationItem = {
  quantity?: number | string | null
  rate?: number | string | null
  discount_percent?: number | string | null
}

interface DebitNoteFormProps {
  debitNote?: Partial<VoucherResponse> // For edit mode or pre-filled data
  sites: Site[]
  godowns: Godown[]
  voucherTypeId: number // Debit Note voucher type ID (pre-fetched)
  isSubmitting: boolean
  onSubmit: (data: DebitNoteFormValues) => void
  searchItems: (query: string) => Promise<Item[]>
  isLoadingItems: boolean
  originalInvoiceItems?: VoucherItemResponse[] // Original purchase invoice items for validation
  originalInvoiceParty?: any // Original invoice party details
  isDisabled?: boolean
}

export const DebitNoteForm: React.FC<DebitNoteFormProps> = ({
  debitNote,
  sites,
  godowns,
  voucherTypeId,
  isSubmitting,
  onSubmit,
  searchItems,
  isLoadingItems,
  originalInvoiceItems = [],
  originalInvoiceParty,
  isDisabled = false
}) => {
  console.log('[DebitNoteForm] Component mounted with props:', {
    hasDebitNote: !!debitNote,
    sitesCount: sites.length,
    godownsCount: godowns.length,
    voucherTypeId,
    originalInvoiceItemsCount: originalInvoiceItems.length,
    hasOriginalInvoiceParty: !!originalInvoiceParty
  })
  const isEditMode = !!debitNote?.id
  const gridRef = useRef<HTMLDivElement>(null)

  // Get user preferences
  const { defaultSiteId, defaultGodownId: preferenceGodownId } = usePreferenceStore()

  // Default warehouse for all items
  const [defaultGodownId, setDefaultGodownId] = useState<number>(
    godowns.length > 0
      ? // Use preference godown if available, otherwise first godown
        preferenceGodownId && godowns.some((g) => g.id === preferenceGodownId)
        ? preferenceGodownId
        : godowns[0]?.id || 0
      : 0
  )

  // Prepare default values
  const defaultValues: Partial<DebitNoteFormValues> = {
    voucher_type_id: voucherTypeId,
    // Use preference site if available
    site_id: defaultSiteId && sites.some((s) => s.id === defaultSiteId) ? defaultSiteId : undefined,
    transaction_date: new Date(),
    total_amount: 0,
    items: []
  }

  // Initialize form first to avoid reference errors
  const form = useForm<DebitNoteFormValues>({
    resolver: zodResolver(debitNoteSchema),
    defaultValues: debitNote?.id
      ? {
          ...defaultValues,
          voucher_type_id: debitNote.voucher_type_id || voucherTypeId,
          party_id: debitNote.party_id || undefined,
          site_id: debitNote.site_id || undefined,
          transaction_date: debitNote.transaction_date
            ? new Date(debitNote.transaction_date)
            : new Date(),
          reference_number: debitNote.reference_number || '',
          reference_invoice_number: (debitNote as any).reference_invoice_number || '',
          narration: debitNote.narration || '',
          linked_voucher_id: debitNote.linked_voucher_id || undefined,
          total_amount: parseFloat(String(debitNote.total_amount || 0)),
          items: []
        }
      : {
          ...defaultValues,
          // When creating from purchase invoice, use party_id from debitNote prop
          party_id: debitNote?.party_id || originalInvoiceParty?.id || undefined,
          site_id: debitNote?.site_id || defaultValues.site_id,
          reference_number: debitNote?.reference_number || '',
          reference_invoice_number: debitNote?.reference_invoice_number || 
            (originalInvoiceItems.length > 0 ? debitNote?.voucher_number : undefined)
        }
  })

  // Initialize items state for the grid
  const [lineItems, setLineItems] = useState<Record<string, unknown>[]>([])

  // Map to track original invoice item quantities
  const [originalQuantities, setOriginalQuantities] = useState<Map<number, number>>(new Map())

  // Watch form values
  const watchedSite = form.watch('site_id')

  // Initialize the line items for the grid AND update form state
  useEffect(() => {
    // Keep track of original invoice item quantities for validation
    if (originalInvoiceItems && originalInvoiceItems.length > 0) {
      const quantityMap = new Map<number, number>()
      originalInvoiceItems.forEach((item) => {
        if (item.id && item.quantity) {
          quantityMap.set(item.id, parseFloat(String(item.quantity)))
        }
      })
      setOriginalQuantities(quantityMap)
    }

    const apiItems = debitNote?.items || []
    console.log('[DebitNoteForm] API items to process:', apiItems)

    let itemsForGrid: Record<string, unknown>[] = []
    let itemsForForm: DebitNoteFormValues['items'] = []
    let calculatedTotal = 0

    // Define calculation helpers
    const calculateDiscountAmount = (item: CalculationItem): number => {
      const quantity = parseFloat(String(item.quantity || 0))
      const rate = parseFloat(String(item.rate || 0))
      const discountPercent = parseFloat(String(item.discount_percent || 0))
      const grossAmount = quantity * rate
      const discountAmount = (grossAmount * discountPercent) / 100
      return Math.round(discountAmount * 100) / 100
    }

    const calculateFinalAmount = (item: CalculationItem): number => {
      const quantity = parseFloat(String(item.quantity || 0))
      const rate = parseFloat(String(item.rate || 0))
      const discountAmount = calculateDiscountAmount(item)
      const grossAmount = quantity * rate
      return Math.round((grossAmount - discountAmount) * 100) / 100
    }

    if (apiItems && apiItems.length > 0) {
      try {
        itemsForGrid = apiItems.map((item) => {
          const quantity = parseFloat(String(item.quantity || 0))
          const rate = parseFloat(String(item.rate || 0))
          const discountPercent = parseFloat(String(item.discount_percent || 0))

          // Use API-provided values for tax and totals if available
          const discountAmount = item.discount_amount
            ? parseFloat(String(item.discount_amount))
            : calculateDiscountAmount({ quantity, rate, discount_percent: discountPercent })
          const taxableAmount = item.taxable_amount
            ? parseFloat(String(item.taxable_amount))
            : calculateFinalAmount({ quantity, rate, discount_percent: discountPercent })
          const taxAmount = item.tax_amount ? parseFloat(String(item.tax_amount)) : 0
          const totalAmount = item.total_amount
            ? parseFloat(String(item.total_amount))
            : taxableAmount + taxAmount

          return {
            id: item.id,
            original_invoice_item_id: item.original_invoice_item_id,
            item_id: item.item_id,
            item_name: item.description || `Item ID: ${item.item_id}`,
            item_code: '',
            description: item.description || '',
            quantity: quantity,
            unit_id: item.unit_id,
            rate: rate,
            discount_percent: discountPercent,
            discount_amount: discountAmount,
            tax_rate_id: item.tax_rate_id,
            taxable_amount: taxableAmount,
            tax_amount: taxAmount,
            amount: totalAmount,
            godown_id: item.godown_id || defaultGodownId
          }
        })

        itemsForForm = itemsForGrid.map((item) => ({
          id: typeof item.id === 'number' ? item.id : undefined,
          original_invoice_item_id:
            typeof item.original_invoice_item_id === 'number'
              ? item.original_invoice_item_id
              : undefined,
          item_id: typeof item.item_id === 'number' ? item.item_id : 0,
          quantity: parseFloat(String(item.quantity || 0)),
          rate: parseFloat(String(item.rate || 0)),
          unit_id: typeof item.unit_id === 'number' ? item.unit_id : 0,
          discount_percent: parseFloat(String(item.discount_percent || 0)),
          tax_rate_id: typeof item.tax_rate_id === 'number' ? item.tax_rate_id : null,
          total_amount: parseFloat(String(item.amount || 0)),
          description: String(item.description || ''),
          godown_id: typeof item.godown_id === 'number' ? item.godown_id : defaultGodownId
        }))

        calculatedTotal = itemsForForm.reduce((sum, item) => sum + (item.total_amount || 0), 0)
      } catch (error) {
        console.error('[DebitNoteForm] Error formatting items:', error)
        itemsForGrid = [
          {
            item_id: '',
            quantity: 1,
            rate: 0,
            discount_percent: 0,
            discount_amount: 0,
            amount: 0,
            description: '',
            godown_id: defaultGodownId
          }
        ]
        itemsForForm = []
        calculatedTotal = 0
      }
    } else {
      itemsForGrid = [
        {
          item_id: '',
          quantity: 1,
          rate: 0,
          discount_percent: 0,
          discount_amount: 0,
          amount: 0,
          description: '',
          godown_id: defaultGodownId
        }
      ]
      itemsForForm = []
      calculatedTotal = 0
    }

    setLineItems(itemsForGrid)
    form.setValue('items', itemsForForm)
    form.setValue('total_amount', calculatedTotal)
  }, [debitNote, isEditMode, defaultGodownId, form, originalInvoiceItems])

  // Update line items when default godown changes
  useEffect(() => {
    if (lineItems.length > 0) {
      setLineItems((currentItems) =>
        currentItems.map((item) => {
          if (!item.godown_id || !item.item_id) {
            return { ...item, godown_id: defaultGodownId }
          }
          return item
        })
      )
    }
  }, [defaultGodownId])

  // Update form state when lineItems change
  useEffect(() => {
    // Skip initial run with placeholder data
    if (!debitNote && !isEditMode && lineItems.length === 1 && !lineItems[0].item_id) {
      console.log('[DebitNoteForm] Skipping initial placeholder row update for new debit note')
      return
    }
    if (isEditMode && !debitNote?.items) {
      console.log('[DebitNoteForm] Skipping update in edit mode with no items')
      return
    }

    console.log('[DebitNoteForm] Updating form state from lineItems:', lineItems)

    const filteredItems = lineItems.filter((item) => {
      const hasItemId = item && (item.item_id || typeof item.item_id === 'number')
      return hasItemId
    })

    console.log('[DebitNoteForm] Filtered items for form update:', filteredItems.length)

    // Update form items array for submission
    const formItems = filteredItems.map((item) => {
      // Ensure all numeric values are properly parsed
      const quantity = parseFloat(String(item.quantity || 0))
      const rate = parseFloat(String(item.rate || 0))
      const discountPercent = parseFloat(String(item.discount_percent || 0))
      const amount = parseFloat(String(item.amount || 0))

      return {
        id: typeof item.id === 'number' ? item.id : undefined,
        original_invoice_item_id:
          typeof item.original_invoice_item_id === 'number'
            ? item.original_invoice_item_id
            : undefined,
        item_id: typeof item.item_id === 'number' ? item.item_id : 0,
        quantity: isNaN(quantity) ? 0 : quantity,
        unit_id: typeof item.unit_id === 'number' ? item.unit_id : 0,
        rate: isNaN(rate) ? 0 : rate,
        discount_percent: isNaN(discountPercent) ? 0 : discountPercent,
        tax_rate_id: typeof item.tax_rate_id === 'number' ? item.tax_rate_id : null,
        total_amount: isNaN(amount) ? 0 : amount,
        description: String(item.description || ''),
        godown_id:
          typeof item.godown_id === 'number'
            ? item.godown_id
            : parseInt(String(item.godown_id || defaultGodownId), 10) || defaultGodownId
      }
    })

    // Only update if we have valid items or we're clearing after deletion
    if (
      formItems.length > 0 ||
      (debitNote && debitNote.items && debitNote.items.length > filteredItems.length)
    ) {
      console.log('[DebitNoteForm] Setting form items:', formItems)
      form.setValue('items', formItems, { shouldValidate: true })

      const total = formItems.reduce((sum, item) => {
        return sum + (item.total_amount || 0)
      }, 0)
      console.log('[DebitNoteForm] Setting total_amount:', total)
      form.setValue('total_amount', total, { shouldValidate: true })
    }
  }, [lineItems, debitNote, isEditMode, form])

  // Update form values when original invoice data changes
  useEffect(() => {
    if (originalInvoiceParty && originalInvoiceParty.id && !isEditMode) {
      form.setValue('party_id', originalInvoiceParty.id)
      console.log('[DebitNoteForm] Setting party_id from originalInvoiceParty:', originalInvoiceParty.id)
    }
  }, [originalInvoiceParty, form, isEditMode])

  // Make sure site_id is properly set when creating from original invoice
  useEffect(() => {
    if (originalInvoiceItems.length > 0 && !isEditMode) {
      // If we have site_id from debitNote props, use it
      const siteId = debitNote?.site_id
      if (siteId) {
        form.setValue('site_id', siteId)
        console.log('[DebitNoteForm] Setting site_id from debitNote:', siteId)
      }
    }
  }, [originalInvoiceItems, debitNote, form, isEditMode])

  // Ensure reference number is set properly
  useEffect(() => {
    if (originalInvoiceItems.length > 0 && !isEditMode && debitNote?.reference_number) {
      // Log received reference number
      console.log('[DebitNoteForm] Original reference_number received:', debitNote.reference_number)
      
      form.setValue('reference_number', debitNote.reference_number)
      console.log('[DebitNoteForm] Setting reference_number:', debitNote.reference_number)
    }
  }, [originalInvoiceItems, debitNote, form, isEditMode])

  // Handle grid data change with validation
  const handleGridDataChange = (newData: Record<string, unknown>[]) => {
    console.log('[DebitNoteForm] Grid data changed:', newData)

    // Apply validation for quantity against original invoice
    const validatedData = newData.map((item) => {
      if (
        item.original_invoice_item_id &&
        originalQuantities.has(Number(item.original_invoice_item_id))
      ) {
        const maxQty = originalQuantities.get(Number(item.original_invoice_item_id))!
        const currentQty = parseFloat(String(item.quantity || 0))

        if (currentQty > maxQty) {
          toast.error(
            `Return quantity (${currentQty}) cannot exceed original invoice quantity (${maxQty})`
          )
          return { ...item, quantity: maxQty }
        }
      }
      return item
    })

    setLineItems(validatedData)
  }

  // Watch party selection
  const watchedPartyId = form.watch('party_id')
  const watchedItems = form.watch('items')

  useEffect(() => {
    if (watchedSite && godowns.length > 0) {
      // Find godowns for this site
      const siteGodowns = godowns.filter((g) => g.site_id === watchedSite)
      if (siteGodowns.length > 0) {
        // If preference godown is in this site, use it
        const preferredInSite = siteGodowns.find((g) => g.id === preferenceGodownId)
        setDefaultGodownId(preferredInSite ? preferredInSite.id : siteGodowns[0].id)
      }
    }
  }, [watchedSite, godowns, preferenceGodownId])

  // Get the party ID for pricing context
  const currentPartyId = watchedPartyId || originalInvoiceParty?.id

  // Submit handler
  // Wrapper for searchItems to convert Item[] to expected format
  const searchProductsWrapper = async (searchTerm: string): Promise<ProductSearchResult[]> => {
    if (!searchTerm || searchTerm.length < 2) return []
    
    try {
      const items = await searchItems(searchTerm)
      return items.map(item => ({
        id: item.id,
        name: item.name || '',
        code: item.code || null, 
        description: item.description || item.name || null,
        tax_rate_id: item.tax_rate_id || null,
        unit_id: item.unit_id || null,
      }))
    } catch (error) {
      console.error('Error searching products:', error)
      return []
    }
  }

  // Handle product selection
  const handleProductSelect = async (product: ProductSearchResult, rowIndex: number): Promise<void> => {
    console.log(`[DebitNoteForm] handleProductSelect for row ${rowIndex}, item ID: ${product.id}`)
    
    // Get the current quantity for the row (default to 1 if not set)
    const currentRow = lineItems[rowIndex] || {}
    const currentQuantity = new Decimal(String(currentRow.quantity || 1))
    const currentSiteId = form.watch('site_id')
    
    console.log(`[DebitNoteForm] Product selection context - quantity: ${currentQuantity}, siteId: ${currentSiteId}`)

    // Try to find this product in original invoice items
    const originalItem = originalInvoiceItems.find(item => item.item_id === product.id)
    let rate = 0
    let discount_percent = 0
    let tax_rate_id = null
    let unit_id = typeof product.unit_id === 'number' ? product.unit_id : null
    let original_invoice_item_id = undefined
    
    if (originalItem) {
      // Use values from original invoice item
      rate = parseFloat(String(originalItem.rate || 0))
      discount_percent = parseFloat(String(originalItem.discount_percent || 0))
      tax_rate_id = originalItem.tax_rate_id
      unit_id = originalItem.unit_id
      original_invoice_item_id = originalItem.id
    } else {
      // Use pricing utility if original item not found
      const pricingContext: PricingContext = {
        itemId: product.id,
        siteId: currentSiteId,
        quantity: currentQuantity
      }

      try {
        const priceDetails = getItemPriceDetails(pricingContext)
        rate = priceDetails ? priceDetails.rate.toNumber() : 0
        discount_percent = priceDetails ? priceDetails.discount_percent.toNumber() : 0
        tax_rate_id = priceDetails ? priceDetails.tax_rate_id : null
      } catch (error) {
        console.error("[DebitNoteForm] Error fetching price details:", error)
        toast.error(`Error calculating price for ${product.name}: ${(error as Error).message}`)
      }
    }

    // Calculate amounts based on rate, quantity and discount
    const discountAmount = (currentQuantity.toNumber() * rate * discount_percent) / 100
    const taxableAmount = currentQuantity.toNumber() * rate - discountAmount
    // Tax amount would be calculated based on taxableAmount and tax_rate_id
    const taxAmount = 0 // Simplified - in real app this would use tax rate
    const finalAmount = taxableAmount + taxAmount

    // Update the grid data state
    setLineItems(currentItems => {
      const newItems = [...currentItems]
      if (newItems[rowIndex]) {
        // Start with existing row data and basic product info
        const updatedRow = {
          ...newItems[rowIndex],
          item_id: product.id,
          item_name: product.name,
          item_code: product.code || '',
          description: product.description || product.name || '',
          godown_id: newItems[rowIndex].godown_id || defaultGodownId,
          quantity: currentQuantity.toNumber(),
          unit_id: unit_id,
          original_invoice_item_id: original_invoice_item_id,
          // Use values from original invoice or pricing utility
          rate: rate,
          discount_percent: discount_percent,
          discount_amount: discountAmount,
          taxable_amount: taxableAmount,
          tax_rate_id: tax_rate_id, 
          tax_amount: taxAmount,
          amount: finalAmount,
        }
        newItems[rowIndex] = updatedRow
        console.log(`[DebitNoteForm] Updated row ${rowIndex} data:`, updatedRow)
      }
      return newItems
    })
  }

  const handleSubmit: SubmitHandler<DebitNoteFormValues> = (data) => {
    console.log('[DebitNoteForm] Submitting form with data:', data)
    onSubmit(data)
  }

  // Column definition with original quantity information
  const columns: GridColumn[] = useMemo(() => [
    { field: 'item_id', header: 'Product', type: 'product', width: '200px' },
    { 
      field: 'quantity', 
      header: 'Return Qty', 
      type: 'number', 
      width: '100px',
      // Add a custom tooltip with original quantity info when hovering
      tooltip: (row) => {
        const originalItemId = row.original_invoice_item_id
        const originalQty = originalItemId ? originalQuantities.get(originalItemId) : null
        return originalQty ? `Original Qty: ${originalQty}` : ''
      }
    },
    { field: 'rate', header: 'Rate', type: 'price', width: '100px' },
    { field: 'discount_percent', header: 'Disc %', type: 'number', width: '80px' },
    // Read-only calculated fields populated by the pricing util
    { field: 'taxable_amount', header: 'Taxable Amt', type: 'price', editable: false, width: '120px' },
    { field: 'tax_amount', header: 'Tax Amt', type: 'price', editable: false, width: '100px' },
    { field: 'amount', header: 'Amount', type: 'price', editable: false, width: '120px' },
    { field: 'description', header: 'Description', type: 'text', width: '180px' },
    { field: 'actions', header: '', type: 'actions', width: '50px' },
  ], [originalQuantities]) // Add dependency on originalQuantities


  try {
    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {/* Vendor Selection */}
              <FormField
                control={form.control}
                name="party_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Vendor *</FormLabel>
                    <FormControl>
                      <PartySelector
                        value={field.value}
                        onChange={field.onChange}
                        disabled={isDisabled || (!!originalInvoiceParty && !isEditMode)}
                        filterPartyType="vendor"
                        placeholder="Select vendor"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Date */}
              <FormField
                control={form.control}
                name="transaction_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date *</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={'outline'}
                            className={cn(
                              'w-full pl-3 text-left font-normal',
                              !field.value && 'text-muted-foreground'
                            )}
                            disabled={isDisabled}
                          >
                            {field.value ? format(field.value, 'PPP') : <span>Pick a date</span>}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) => date > new Date() || date < new Date('1900-01-01')}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Site */}
              <FormField
                control={form.control}
                name="site_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Site</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(value ? Number(value) : null)}
                      value={field.value?.toString() || ''}
                      disabled={isDisabled}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select site" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {sites.map((site) => (
                          <SelectItem key={site.id} value={site.id.toString()}>
                            {site.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Reference Number */}
              <FormField
                control={form.control}
                name="reference_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reference Number</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="e.g. Original Invoice No."
                        disabled={isDisabled}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Default Warehouse */}
              <FormItem>
                <FormLabel>Default Warehouse</FormLabel>
                <Select
                  onValueChange={(value) => setDefaultGodownId(Number(value))}
                  value={defaultGodownId.toString()}
                  disabled={isDisabled}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select warehouse" />
                  </SelectTrigger>
                  <SelectContent>
                    {godowns
                      .filter((g) => !watchedSite || g.site_id === watchedSite)
                      .map((godown) => (
                        <SelectItem key={godown.id} value={godown.id.toString()}>
                          {godown.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground mt-1">
                  This warehouse will be used for all new items
                </p>
              </FormItem>
            </div>

            {/* Narration */}
            <FormField
              control={form.control}
              name="narration"
              render={({ field }) => (
                <FormItem className="mb-6">
                  <FormLabel>Narration</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Additional notes..."
                      className="resize-none"
                      rows={3}
                      disabled={isDisabled}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Items Grid */}
        <Card>
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-4">Items to Return</h3>
            <EditableDataGrid
              ref={gridRef}
              data={lineItems}
              setData={setLineItems}
              columns={columns}
              searchFunction={searchProductsWrapper}
              onProductSelect={handleProductSelect}
              isLoading={isLoadingItems}
              isDisabled={isSubmitting || isDisabled}
              godowns={godowns}
              defaultGodownId={defaultGodownId}
              onDefaultGodownChange={setDefaultGodownId}
            />
          </CardContent>
        </Card>

        {/* Summary */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold">Total Amount:</span>
              <span className="text-2xl font-bold">
                ₹{form.watch('total_amount')?.toFixed(2) || '0.00'}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button type="submit" disabled={isSubmitting || isDisabled}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEditMode ? 'Update Debit Note' : 'Create Debit Note'}
          </Button>
        </div>
      </form>
    </Form>
  )
  } catch (error) {
    console.error('[DebitNoteForm] Error rendering form:', error)
    return (
      <div className="p-4 border border-red-500 rounded">
        <p className="text-red-500">Error rendering form: {(error as Error).message}</p>
      </div>
    )
  }
}
